import React, { useMemo, useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { applyLang } from "../../../i18n";
import { ASSET_URL } from "../../../config";
import AiContentSlot from "../../../components/AiContentSlot";
import renderIcon from "../../../components/renderIcon";
import HebcalService from "../../../services/hebcal";
import { restPeriodTitle, restPeriodRowLabel } from "../../../services/restPeriodDisplay";
import logger from "../../../services/logger"
import { formatShabbatSessionDateRange } from "../../../lib/shabbatDateRange";

const chabbatStyles = `
/* ── HERO ── */
.tr-chabbat__hero {
    position: relative;
    height: clamp(320px, 45vw, 520px);
    overflow: hidden;
    border-radius: 0 0 48px 48px;
    background: linear-gradient(135deg, #1b579e 0%, #0f3a6e 100%);
    margin-bottom: 32px;
}
.tr-chabbat__hero-img {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    object-fit: cover;
}
.tr-chabbat__hero-wash {
    position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,.4) 100%);
}
.tr-chabbat__hero-title {
    position: absolute; inset: auto 0 30% 0;
    text-align: center;
    color: white;
    font-size: clamp(28px, 5vw, 56px);
    font-weight: 800;
    letter-spacing: -.02em;
    text-shadow: 0 2px 16px rgba(0,0,0,.3);
    padding: 0 24px;
    text-wrap: balance;
}

/* ── HERO sans image (fallback) ── */
.tr-chabbat__hero--noimg {
    height: auto;
    border-radius: 0;
    background: transparent;
    margin-bottom: 8px;
}
.tr-chabbat__hero--noimg .tr-chabbat__hero-title {
    position: static;
    color: #1a1a2e;
    text-shadow: none;
    padding: clamp(40px, 6vw, 72px) 24px 16px;
}

/* ── BREADCRUMB ── */
.tr-chabbat__crumb {
    text-align: center;
    margin: 0 auto 24px;
    padding: 0 16px;
    color: #4b5563;
    font-size: 15px;
}
.tr-chabbat__crumb b { color: #1a1a2e; }

/* ── SHABBAT TIMES ── */
.tr-chabbat__times {
    text-align: center;
    margin: 0 auto 40px;
    padding: 0 16px;
    max-width: 720px;
}
.tr-chabbat__times-title {
    font-size: clamp(22px, 3.2vw, 34px);
    font-weight: 800;
    color: #1a1a2e;
    margin: 0 0 8px;
    letter-spacing: -.01em;
}
.tr-chabbat__times-parasha {
    font-size: clamp(16px, 2.2vw, 22px);
    font-weight: 700;
    color: #1a1a2e;
    margin: 0;
}
.tr-chabbat__times-date {
    color: #6b7280;
    font-size: 14px;
    margin-top: 4px;
}
.tr-chabbat__times-grid {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 28px;
    flex-wrap: wrap;
}
.tr-chabbat__time-tile {
    background: color-mix(in srgb, var(--primary, #1b579e) 10%, white);
    border-radius: 18px;
    padding: 22px 28px;
    min-width: 160px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0,0,0,.04);
}
.tr-chabbat__time-icon {
    color: var(--primary, #1b579e);
    margin-bottom: 8px;
    display: inline-flex;
}
.tr-chabbat__time-label {
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 4px;
}
.tr-chabbat__time-value {
    font-size: 22px;
    font-weight: 800;
    color: #1a1a2e;
    letter-spacing: -.01em;
}
.tr-chabbat__time-date { color: #6b7280; font-size: 12px; margin-top: 3px; }

/* ── CARD SESSIONS ── */
.tr-chabbat__container {
    max-width: 880px;
    margin: 0 auto;
    padding: 0 16px;
}
.tr-chabbat__card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    padding: 28px;
    box-shadow: 0 8px 24px rgba(0,0,0,.04);
    margin-bottom: 24px;
}
.tr-chabbat__intro { margin-bottom: 32px; }
.tr-chabbat__section-label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: .12em;
    color: #94a3b8;
    font-weight: 700;
    margin-bottom: 16px;
}
.tr-chabbat__sessions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 360px;
    overflow-y: auto;
}
.tr-chabbat__session {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #fff;
    cursor: pointer;
    transition: all .15s;
}
.tr-chabbat__session:hover { border-color: var(--primary, #1b579e); background: color-mix(in srgb, var(--primary, #1b579e) 5%, white); }
.tr-chabbat__session--selected { border: 2px solid var(--primary, #1b579e); background: color-mix(in srgb, var(--primary, #1b579e) 8%, white); }
.tr-chabbat__session--locked { opacity: .55; cursor: not-allowed; }
.tr-chabbat__session-left { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.tr-chabbat__session-date { font-weight: 700; font-size: 15px; color: #0f172a; display: flex; align-items: center; gap: 6px; }
.tr-chabbat__session-label { font-size: 13px; color: #64748b; }
.tr-chabbat__links { margin-top: 20px; padding-top: 20px; border-top: 1px solid #f1f5f9; }
.tr-chabbat__links--inline { margin: 4px 0 8px 12px; padding-top: 0; border-top: 0; }
.tr-chabbat__link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-radius: 12px;
    background: var(--primary, #1b579e);
    color: #fff !important;
    text-decoration: none !important;
    font-weight: 600;
    margin-bottom: 8px;
    transition: transform .15s, box-shadow .15s;
}
.tr-chabbat__link:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,.15); color: #fff !important; }
.tr-chabbat__link span,
.tr-chabbat__link svg { color: #fff !important; }

.tr-chabbat__empty {
    padding: 24px;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 12px;
    text-align: center;
    color: #64748b;
}

/* ── ÉROUV ── */
.tr-chabbat__eruv {
    max-width: 880px;
    margin: 0 auto 64px;
    padding: 24px;
    border-radius: 18px;
    background: #fff;
    border: 1px solid #e2e8f0;
    box-shadow: 0 8px 24px rgba(0,0,0,.04);
}
.tr-chabbat__eruv-head {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #1a1a2e;
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 8px;
}
.tr-chabbat__eruv-pill {
    margin-left: auto;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 700;
}
.tr-chabbat__eruv-pill--yes { background: #dcfce7; color: #15803d; }
.tr-chabbat__eruv-pill--no { background: #fee2e2; color: #b91c1c; }
.tr-chabbat__eruv-pill--partial { background: #fef3c7; color: #b45309; }
.tr-chabbat__eruv-comment {
    color: #4b5563;
    font-size: 14px;
    line-height: 1.5;
    margin-top: 8px;
}

.tr-chabbat__inactive {
    text-align: center;
    padding: 96px 16px;
    color: #6b7280;
}
.tr-chabbat__inactive-title {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 8px;
}
`;

function formatTime(d, tz) {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date.getTime())) return "—";
    // Heure murale dans le fuseau de l'org (pas celui du serveur/navigateur)
    return new Intl.DateTimeFormat("fr", {
        hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz || undefined,
    }).format(date);
}

function formatDate(d, tz) {
    return new Intl.DateTimeFormat("fr", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: tz }).format(d);
}

export default function Chabbat({ context, loading }) {
    const { org, config, travel, lang } = context;
    // `langValue` = STRING (locale) posée par RouterService — source de vérité pour
    // le formatage Intl. On la lit du contexte ; fallback robuste sur `lang` (objet
    // {value} OU string) pour ne jamais passer un objet à Intl.DateTimeFormat.
    const langValue = context.langValue || (typeof lang === "string" ? lang : lang?.value) || "fr";

    const siteId = travel?.siteId;
    const isModuleActive = !!travel?.chabbat?.active;

    const extensionKey = siteId ? `TRAVEL.CHABAT.${siteId}` : null;
    const groupeRaw = extensionKey ? org.eventGroupesByExtension?.[extensionKey] : null;
    const groupe = useMemo(() => (groupeRaw ? applyLang(groupeRaw, lang) : null), [groupeRaw, lang]);

    const sessions = useMemo(() => {
        if (!groupe) return [];
        const now = Date.now();
        return groupe.sessions
            .map(s => applyLang(s, lang))
            .filter(s => {
                const debut = s.dateDebut ? new Date(s.dateDebut).getTime() : 0;
                const fin = new Date(s.dateFin).getTime();
                return now >= debut && now <= fin;
            });
    }, [groupe, lang]);

    const [selectedId, setSelectedId] = useState(null);

    const restPeriod = useMemo(() => {
        if (!org?.lat || !org?.long) return null;
        try {
            const hb = new HebcalService(org);
            return hb.getNextRestPeriod(new Date());
        } catch (e) {
            logger.error({ err: e }, "Hebcal error");
            return null;
        }
    }, [org?.lat, org?.long, org?.israel, org?.zmanimShitat, org?.chabadCandleLightingMinutes]);

    // Érouv (depuis travel.jewishInfo)
    const jewishInfo = useMemo(() => applyLang(travel?.jewishInfo || {}, lang), [travel?.jewishInfo, lang]);
    const eruv = jewishInfo.eruv;
    const eruvComment = jewishInfo.eruvComment;
    const eruvLabel = {
        yes: t`Oui`,
        no: t`Non`,
        partial: t`Partiel`,
    }[eruv];

    const title = groupe?.title || groupe?.label || t`Shabbat à ${org.ville || org.name}`;
    const description = groupe?.description || config["meta.description"] || t`Réservez votre repas de Shabbat à ${org.ville || org.name}`;
    const heroImage = groupe?.image ? (groupe.image.startsWith("http") ? groupe.image : `${ASSET_URL}${groupe.image}`) : null;

    return (
        <div className="travel-page travel-page--chabbat tr-chabbat">
            <style dangerouslySetInnerHTML={{ __html: chabbatStyles }} />
            <Meta context={context} title={title} description={description} image={heroImage} />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">

                {!isModuleActive && (
                    <div className="tr-chabbat__inactive">
                        <div className="tr-chabbat__inactive-title">
                            <Trans>Module Chabbat désactivé</Trans>
                        </div>
                        <div>
                            <Trans>Ce service n'est pas activé pour le moment.</Trans>
                        </div>
                    </div>
                )}

                {isModuleActive && (
                    <>
                        {/* HERO */}
                        {heroImage ? (
                            <section className="tr-chabbat__hero">
                                <img src={heroImage} alt={title} className="tr-chabbat__hero-img" />
                                <div className="tr-chabbat__hero-wash" />
                                <h1 className="tr-chabbat__hero-title">{title}</h1>
                            </section>
                        ) : (
                            <section className="tr-chabbat__hero tr-chabbat__hero--noimg">
                                <h1 className="tr-chabbat__hero-title">{title}</h1>
                            </section>
                        )}

                        {/* Breadcrumb-like context line */}
                        {context.render !== "embed" && <div className="tr-chabbat__crumb">
                            <b>{org.name}</b>
                            {org.ville && <> — {t`Shabbat à ${org.ville}`}</>}
                        </div>}

                        {/* SHABBAT TIMES */}
                        {restPeriod && (
                            <section className="tr-chabbat__times">
                                <h2 className="tr-chabbat__times-title">
                                    {restPeriodTitle(restPeriod, langValue)}
                                </h2>
                                {restPeriod.occasion.parasha && (
                                    <p className="tr-chabbat__times-parasha">
                                        <Trans>Parasha</Trans> {langValue === "he" ? restPeriod.occasion.parasha.hebrew : restPeriod.occasion.parasha.latin}
                                    </p>
                                )}
                                <div className="tr-chabbat__times-date">
                                    {formatDate(restPeriod.start, restPeriod.timeZone)} - {formatDate(restPeriod.end, restPeriod.timeZone)}
                                </div>

                                <div className="tr-chabbat__times-grid">
                                    {restPeriod.schedule.map((item, index) => <div className="tr-chabbat__time-tile" key={`${item.kind}-${index}`}>
                                        <div className="tr-chabbat__time-icon">
                                            {renderIcon({ name: item.kind === "exit" ? "IconGlassFull" : "IconCandle", source: "tabler" }, 32)}
                                        </div>
                                        <div className="tr-chabbat__time-label">
                                            {restPeriodRowLabel(item, restPeriod)}
                                        </div>
                                        <div className="tr-chabbat__time-value">
                                            {formatTime(item.at, restPeriod.timeZone)}
                                        </div>
                                        <div className="tr-chabbat__time-date">{formatDate(item.at, restPeriod.timeZone)}</div>
                                    </div>)}
                                </div>
                            </section>
                        )}

                        {/* SESSIONS */}
                        <div className="tr-chabbat__container">
                            {groupe?.aiContentBlockId && (
                                <div className="tr-chabbat__intro">
                                    <AiContentSlot blockId={groupe.aiContentBlockId} lang={langValue} />
                                </div>
                            )}

                            <div className="tr-chabbat__card">
                                <div className="tr-chabbat__section-label">
                                    <Trans>Choisissez votre Shabbat</Trans>
                                </div>

                                {sessions.length === 0 ? (
                                    <div className="tr-chabbat__empty">
                                        <Trans>Aucune session disponible pour le moment.</Trans>
                                    </div>
                                ) : (
                                    <div className="tr-chabbat__sessions">
                                        {sessions.map(sess => {
                                            const isSelected = sess.id === selectedId;
                                            const isAvail = sess.active;
                                            const cls = [
                                                "tr-chabbat__session",
                                                isSelected ? "tr-chabbat__session--selected" : "",
                                                !isAvail ? "tr-chabbat__session--locked" : "",
                                            ].filter(Boolean).join(" ");
                                            return (
                                                <React.Fragment key={sess.id}>
                                                    <div
                                                        className={cls}
                                                        // Toggle : reclic sur la session sélectionnée la referme.
                                                        onClick={() => isAvail && setSelectedId(isSelected ? null : sess.id)}
                                                    >
                                                        <div className="tr-chabbat__session-left">
                                                            <div className="tr-chabbat__session-date">
                                                                {renderIcon({ name: "IconCalendar", source: "tabler" }, 14)}
                                                                {/* Exception module Chabbat : plage vendredi/samedi dérivée du
                                                                    samedi de la session. Fallback dateEvent. */}
                                                                <bdi dir={groupeRaw?.shabbatDateFormat === "numeric" ? "ltr" : "auto"}>{formatShabbatSessionDateRange(sess.dateFin || sess.dateEvent, langValue, groupeRaw?.shabbatDateFormat)}</bdi>
                                                            </div>
                                                            {(sess.title || sess.name) && (
                                                                <div className="tr-chabbat__session-label">{sess.title || sess.name}</div>
                                                            )}
                                                        </div>
                                                        {!isAvail
                                                            ? renderIcon({ name: "IconLock", source: "tabler" }, 16)
                                                            : (
                                                                <span style={{
                                                                    display: 'inline-flex',
                                                                    transition: 'transform 0.2s',
                                                                    transform: isSelected ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                }}>
                                                                    {renderIcon({ name: "IconChevronRight", source: "tabler" }, 18)}
                                                                </span>
                                                            )
                                                        }
                                                    </div>

                                                    {/* Liens inline juste sous la session ouverte plutôt qu'en bas de liste. */}
                                                    {isSelected && (
                                                        <div className="tr-chabbat__links tr-chabbat__links--inline">
                                                            {(sess.links || []).length === 0 ? (
                                                                <div className="tr-chabbat__empty">
                                                                    <Trans>Aucune inscription disponible pour cette session.</Trans>
                                                                </div>
                                                            ) : (
                                                                (sess.links || []).map((link, i) => {
                                                                    const tLink = applyLang(link, lang);
                                                                    return (
                                                                        <a key={i} href={tLink.url} target="_blank" rel="noreferrer" className="tr-chabbat__link">
                                                                            <span>{tLink.name}</span>
                                                                            {renderIcon({ name: "IconChevronRight", source: "tabler" }, 18)}
                                                                        </a>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ÉROUV */}
                        {eruv && (
                            <div className="tr-chabbat__eruv">
                                <div className="tr-chabbat__eruv-head">
                                    {renderIcon({ name: "IconFence", source: "tabler" }, 20)}
                                    <span><Trans>Érouv à {org.ville || org.name}</Trans></span>
                                    <span className={`tr-chabbat__eruv-pill tr-chabbat__eruv-pill--${eruv}`}>
                                        {eruvLabel}
                                    </span>
                                </div>
                                {eruvComment && (
                                    <div className="tr-chabbat__eruv-comment">{eruvComment}</div>
                                )}
                            </div>
                        )}
                    </>
                )}

            </main>

            <Footer context={context} />
        </div>
    );
}
